import React from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Typography, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Markdown from 'react-markdown';
import { ThinkingStep } from '../../types';
import { r } from 'react-router/dist/development/index-react-server-client-Bi_fx8qz';

export interface ChainOfThoughtProps {
  steps: ThinkingStep[];
  isLoading?: boolean;
  /** Hide specific agents (e.g., ["SeAgent"]) */
  hideAgents?: string[];
  /** Max panel height (px or css string) */
  maxHeight?: number | string;
  /** Optional title shown while collapsed */
  summaryText?: string | ((visibleSteps: ThinkingStep[], isLoading?: boolean) => string);
  /** Optional custom renderer for a step */
  renderStep?: (step: ThinkingStep, index: number) => React.ReactNode;
  /** Start expanded or not */
  defaultExpanded?: boolean;
}

const defaultSummary = (visibleSteps: ThinkingStep[], isLoading?: boolean) =>
   visibleSteps.length > 0 && !isLoading ? `Thought of ${visibleSteps.length} steps` :  'Enantio is thinking…' ;

const generateTitle = (agent: string) => {
  console.log(agent.slice(0,2))
  switch(agent.slice(0,2)) {
    case 'Fi':
      return 'Subjective Feeling Agent'
    case 'Ti':
      return 'Subjective Thinking Agent'
    case 'Te':
      return 'Objective Thinking Agent'
    case 'Fe':
      return 'Objective Feeling Agent'
    case 'Ne': 
      return 'Objective Intuition Agent';
    case 'Ni': 
      return 'Subjective Intuition Agent'
  }
}

const StepItem: React.FC<{ step: ThinkingStep; index: number }> = ({ step, index }) => (
  <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
<Accordion
  disableGutters
  elevation={0}
  sx={{
    backgroundColor: 'transparent',
    '&:before': { display: 'none' }, // removes default divider line
    boxShadow: 'none',
    border: 'none',
  }}
>
  <AccordionSummary
    //expandIcon={<ExpandMoreIcon />}
    sx={{
      //backgroundColor: 'background.default',
      minHeight: 0,
      '& .MuiAccordionSummary-content': { my: 0.5 },
    }}
  >
    <Typography variant="subtitle2">
      {generateTitle(step.agent)}
    </Typography>
  </AccordionSummary>

  <AccordionDetails
    sx={{
      //backgroundColor: 'background.default',
      borderTop: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
      <Typography
        variant="caption"
        display="block"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.secondary' }}
      >
        <Markdown>{step.output || ''}</Markdown>
      </Typography>
    </Box>
  </AccordionDetails>
</Accordion>

  </Box>
);

const ChainOfThought: React.FC<ChainOfThoughtProps> = ({
  steps,
  isLoading,
  hideAgents = ['SeAgent'],
  maxHeight = 300,
  summaryText = defaultSummary,
  renderStep,
  defaultExpanded = false,
}) => {
  const visible = React.useMemo(
    () => steps.filter(s => !hideAgents.includes(s.agent)),
    [steps, hideAgents]
  );

  const summary =
    typeof summaryText === 'function' ? summaryText(visible, isLoading) : summaryText;

  if(!isLoading)  {
    return (
      <></>
    )
  }

  return (
    <Box sx={{ px: { xs: 0, sm: 2 }, py: 2 }}>
      <Accordion
        defaultExpanded={defaultExpanded}
        sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="thinking-process-content"
          id="thinking-process-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isLoading && <CircularProgress size={20} sx={{ mr: 2 }} />}
            <Typography variant="body2" color="text.secondary">{summary}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight, overflowY: 'auto', p: 0 }}>
          {visible.map((step, index) =>
            renderStep ? renderStep(step, index) : <StepItem key={index} step={step} index={index} />
          )}
          {isLoading && visible.length < 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">Thinking of the next step…</Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ChainOfThought;
